// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

export type TimeRangeValue = 'past_365_days' | 'past_2_years' | 'alltime';

export interface TimeRangeOption {
  readonly value: TimeRangeValue;
  readonly label: string;
  readonly calculation: { unit: 'days' | 'months'; amount: number } | null;
}

export class TimeRange {
  static readonly DEFAULT: TimeRangeValue = 'past_2_years';

  static readonly VALID_VALUES: readonly TimeRangeValue[] = [
    'past_365_days',
    'past_2_years',
    'alltime'
  ] as const;

  static readonly OPTIONS: readonly TimeRangeOption[] = [
    { value: 'past_365_days', label: 'Past 365 days', calculation: { unit: 'days', amount: 365 } },
    { value: 'past_2_years', label: 'Past 2 years', calculation: { unit: 'months', amount: 24 } },
    { value: 'alltime', label: 'All time', calculation: null },
  ] as const;

  static isValid(value: string | null | undefined): value is TimeRangeValue {
    return !!value && this.VALID_VALUES.includes(value as TimeRangeValue);
  }

  static getOption(value: TimeRangeValue): TimeRangeOption {
    const option = this.OPTIONS.find(opt => opt.value === value);
    if (!option) throw new Error(`Invalid time range: ${value}`);
    return option;
  }

  static getLabel(value: TimeRangeValue): string {
    return this.getOption(value).label;
  }

  static getStartDate(value: TimeRangeValue): Date | null {
    const calc = this.getOption(value).calculation;
    if (!calc) return null;

    const start = new Date();
    if (calc.unit === 'days') {
      start.setDate(start.getDate() - calc.amount);
    } else {
      start.setMonth(start.getMonth() - calc.amount);
    }
    return start;
  }

  static getDateRangeDisplay(value: TimeRangeValue): string {
    const startDate = this.getStartDate(value);
    if (!startDate) return '';

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formatted = `${months[startDate.getMonth()]} ${startDate.getDate()}, ${startDate.getFullYear()}`;
    return `${formatted} → Today`;
  }

  static fromQueryParam(value: string | null | undefined): TimeRangeValue {
    return this.isValid(value) ? value : this.DEFAULT;
  }
}
